import * as Dialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';

interface ModalProps {
  /** Nome acessível do diálogo (renderizado sr-only — cada consumidor mantém seu próprio título visível) */
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** `centro` (default) = diálogo centralizado; `esquerda` = drawer lateral. */
  posicao?: 'centro' | 'esquerda';
  /** Controla `Dialog.Root open`. Default `true` — consumidores atuais montam/desmontam o Modal. */
  aberto?: boolean;
}

/**
 * Wrapper fino sobre @radix-ui/react-dialog: portal, overlay, foco
 * (focus trap), Escape-to-close e clique-fora — tudo que os modais
 * hand-rolled do projeto não tinham. Não impõe nenhum estilo visual
 * ao card em si; isso continua responsabilidade de `children`.
 *
 * Em `posicao="esquerda"` o `Dialog.Content` *é* o painel (não a
 * viewport flex), então o outside-click nativo do Radix já fecha —
 * sem o `onClick` de `e.target === e.currentTarget` do centro.
 */
export function Modal({
  title,
  onClose,
  children,
  posicao = 'centro',
  aberto = true,
}: ModalProps) {
  /** Só fecha quando o clique acerta a própria área de backdrop, nunca um
   *  descendente (o card). Fica nos dois níveis — container de rolagem e
   *  wrapper de centralização — porque qualquer um dos dois pode ser a área
   *  visível de backdrop dependendo da altura do card. */
  function fecharSeForBackdrop(e: React.MouseEvent<HTMLElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <Dialog.Root
      open={aberto}
      onOpenChange={isOpen => { if (!isOpen) onClose(); }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
        {posicao === 'esquerda' ? (
          <Dialog.Content
            asChild
            aria-describedby={undefined}
            aria-modal="true"
          >
            <motion.div
              className="fixed inset-y-0 left-0 z-50 w-[min(20rem,90vw)] pl-[env(safe-area-inset-left)]"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
            >
              <Dialog.Title className="sr-only">{title}</Dialog.Title>
              {children}
            </motion.div>
          </Dialog.Content>
        ) : (
          <Dialog.Content
            className="fixed inset-0 z-50 overflow-y-auto overscroll-contain py-6 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]"
            aria-describedby={undefined}
            aria-modal="true"
            // Dialog.Content cobre a viewport inteira (para centralizar o
            // card via flex), então o DismissableLayer do Radix nunca
            // considera nenhum clique real "fora" dele — a detecção nativa
            // de outside-click do Radix nunca dispara aqui. Replicamos o
            // mesmo padrão que os modais hand-rolled já usavam
            // (`e.target === e.currentTarget`): só fecha quando o clique
            // acerta o próprio Content (a área de "backdrop" visível através
            // do padding do flex), nunca quando acerta um descendente (o
            // card em si).
            onClick={fecharSeForBackdrop}
          >
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            {/* O Content é quem rola (conteúdo mais alto que a viewport em
                1366×768 ou celular deitado); este wrapper `min-h-full` é quem
                centraliza. Centralizar no próprio container de rolagem cortaria
                o topo do card quando ele não cabe. */}
            <div
              className="flex min-h-full items-center justify-center"
              onClick={fecharSeForBackdrop}
            >
              {children}
            </div>
          </Dialog.Content>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
}
